import type { MessageContent, SlotOccurrence } from "../types"

const uuidSource =
  "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}"
const uuidPattern = new RegExp(`^(?:${uuidSource})$`)
const markerSource = `\\{\\{slot:(${uuidSource})\\}\\}`

export interface SlotMarker {
  slotId: string
  offset: number
  length: number
}

export interface SlotGroup {
  slotId: string
  label: string
  occurrences: SlotOccurrence[]
}

/** Wrap an existing UUID; identity creation belongs to the caller. */
export function createSlotMarker(id: string): string {
  if (id.length !== 36 || !uuidPattern.test(id)) {
    throw new RangeError("A slot ID must be a UUID")
  }
  return `{{slot:${id}}}`
}

/** Raw source scan, also usable by token-aware editors without markup parsing. */
export function findSlotMarkers(source: string): SlotMarker[] {
  return Array.from(
    source.matchAll(new RegExp(markerSource, "g")),
    (match) => ({
      slotId: match[1],
      offset: match.index!,
      length: match[0].length,
    }),
  )
}

export function isWholeSlotMarker(value: string): boolean {
  const match = new RegExp(markerSource).exec(value)
  return match !== null && match.index === 0 && match[0].length === value.length
}

function withoutMarkers(label: string): string {
  return label.replace(new RegExp(markerSource, "g"), "").trim()
}

interface RawLinkDestination {
  offset: number
  url: string
  label: string
}

function htmlLabel(source: string): string {
  const references: Record<string, string> = {
    amp: "&",
    lt: "<",
    gt: ">",
    quot: '"',
    apos: "'",
  }
  return withoutMarkers(
    source
      .replace(/<[^>]*>/g, "")
      .replace(
        /&(amp|lt|gt|quot|apos);/g,
        (_, name: string) => references[name],
      ),
  )
}

/**
 * Lexical hints for common raw links, NOT a Telegram parser or validator.
 * They only classify destination spans; source is never decoded or rewritten.
 * Full escaping, code-block semantics and malformed grammar belong to codecs.
 */
function findMarkupLinks(
  source: string,
  mode: "HTML" | "Markdown" | "MarkdownV2",
): RawLinkDestination[] {
  const links: RawLinkDestination[] = []
  if (mode === "HTML") {
    const anchors = /(<a\b(?:[^<>"']|"[^"]*"|'[^']*')*>)([\s\S]*?)<\/a\s*>/gi
    for (const anchor of source.matchAll(anchors)) {
      const attributes =
        /([^\s=<>/]+)(\s*=\s*)(?:"([^"]*)"|'([^']*)'|([^\s>]+))/g
      for (const attribute of anchor[1].matchAll(attributes)) {
        if (attribute[1].toLowerCase() !== "href") continue
        const quoted = attribute[3] !== undefined || attribute[4] !== undefined
        links.push({
          offset:
            anchor.index! +
            attribute.index! +
            attribute[1].length +
            attribute[2].length +
            (quoted ? 1 : 0),
          url: attribute[3] ?? attribute[4] ?? attribute[5],
          label: htmlLabel(anchor[2]),
        })
      }
    }
  } else {
    const markdownLinks = /\[((?:\\.|[^\]\\\r\n])*)\]\(((?:\\.|[^)\\\r\n])*)\)/g
    for (const link of source.matchAll(markdownLinks)) {
      // An escaped opening bracket is literal text, not a link.
      let precedingSlashes = 0
      for (
        let index = link.index! - 1;
        index >= 0 && source[index] === "\\";
        index--
      )
        precedingSlashes++
      if (precedingSlashes % 2 !== 0) continue
      links.push({
        offset: link.index! + link[1].length + 3,
        url: link[2],
        label: withoutMarkers(link[1].replace(/\\([\s\S])/g, "$1")),
      })
    }
  }
  return links
}

export function collectSlotOccurrences(
  content: MessageContent,
): SlotOccurrence[] {
  const links =
    content.formatting.kind === "markup"
      ? findMarkupLinks(content.text, content.formatting.parseMode)
      : []
  const located: { position: number; occurrence: SlotOccurrence }[] = []
  for (const marker of findSlotMarkers(content.text)) {
    const link = links.find(
      (link) =>
        marker.offset >= link.offset &&
        marker.offset < link.offset + link.url.length,
    )
    // A marker embedded in a URL is invalid, not a bare-text occurrence.
    if (link && !isWholeSlotMarker(link.url)) continue
    located.push({
      position: marker.offset,
      occurrence: {
        ...marker,
        document: "text",
        path: "/text",
        label: link?.label ?? "",
      },
    })
  }
  if (content.formatting.kind === "entities") {
    content.formatting.entities.forEach((entity, index) => {
      if (entity.type !== "text_link" || !isWholeSlotMarker(entity.url)) return
      located.push({
        position: entity.offset,
        occurrence: {
          ...findSlotMarkers(entity.url)[0],
          document: "entity-url",
          path: `/formatting/entities/${index}/url`,
          label: withoutMarkers(
            content.text.slice(entity.offset, entity.offset + entity.length),
          ),
        },
      })
    })
  }
  const occurrences = located
    .sort((a, b) => a.position - b.position)
    .map((item) => item.occurrence)

  content.buttons.forEach((row, rowIndex) => {
    row.forEach((button, buttonIndex) => {
      const path = `/buttons/${rowIndex}/${buttonIndex}`
      const label = withoutMarkers(button.text)
      for (const marker of findSlotMarkers(button.text)) {
        occurrences.push({
          ...marker,
          document: "button-text",
          path: `${path}/text`,
          label,
        })
      }
      if (isWholeSlotMarker(button.url)) {
        occurrences.push({
          ...findSlotMarkers(button.url)[0],
          document: "button-url",
          path: `${path}/url`,
          label,
        })
      }
    })
  })
  return occurrences
}

/** Pass one UUID's occurrences and its 1-based position among all groups. */
export function getSlotLabel(
  occurrences: readonly SlotOccurrence[],
  ordinal = 1,
): string {
  const labels = new Set(
    occurrences
      .map((occurrence) => withoutMarkers(occurrence.label))
      .filter(Boolean),
  )
  return labels.size === 1 ? [...labels][0] : `Ссылка ${ordinal}`
}

/** Input order is source order: message text/native links, then button rows. */
export function groupSlotOccurrences(
  occurrences: readonly SlotOccurrence[],
): SlotGroup[] {
  const groups = new Map<string, SlotOccurrence[]>()
  for (const occurrence of occurrences) {
    const group = groups.get(occurrence.slotId)
    if (group) group.push(occurrence)
    else groups.set(occurrence.slotId, [occurrence])
  }
  return Array.from(groups, ([slotId, group], index) => ({
    slotId,
    label: getSlotLabel(group, index + 1),
    occurrences: group,
  }))
}
